<!-- ============================================================
  PrivateBookingManagement.vue — "จัดการการจอง Private", the Private-lesson
  counterpart of BookingManagement.vue ("จัดการการจอง" for group classes).
  Deliberately mirrors that page's layout/interaction pattern (id-calendar
  date picker + id-data-grid with inline view/checkin/edit/delete icon
  columns, same confirm-modal shapes) so managing Private bookings feels
  like the same system as managing group-class bookings, per the project's
  "private and group class booking management should follow the same
  format" rule. The actual add/edit form lives in the shared
  PrivateBookingEditDialog.vue (also used by Dashboard.vue's quick "เพิ่มการจอง"
  flow) so there is one implementation, not two drifting copies.
============================================================ -->
<template>
    <div>
        <div class="pg-head">
            <div class="pg-ico"><span class="mdi mdi-account-heart-outline"></span></div>
            <div>
                <div class="id-h1">{{ $t('privateBooking.managementTitle') }}</div>
                <div class="pg-sub">{{ $t('privateBooking.viewSubtitle') }}</div>
            </div>
        </div>

        <div class="row" style="justify-content:space-between;align-items:center;margin-bottom:12px;gap:10px;flex-wrap:wrap">
            <div class="isk-sec">{{ viewMode === 'month' ? $t('privateBooking.bookingsInMonth') : $t('bookingMgmt.allBookingsToday') }}</div>
            <div class="reserve-datebar" style="margin-bottom:0">
                <button class="pb-today" type="button" @click="goToday">{{ $t('privateBooking.today') }}</button>
                <span class="mdi mdi-calendar-check"></span> <b>{{ rangeLabel }}</b>
            </div>
        </div>

        <!-- month overview chip — quick totals for the whole displayed month -->
        <div v-if="viewMode === 'month' && displayRows.length" style="margin:-4px 0 12px">
            <span class="badge badge-primary">{{ $t('privateBooking.rangeSummary', { count: summary.count, coaches: summary.coaches, days: summary.days }) }}</span>
        </div>

        <div class="grid2 grid2-booking">
            <div class="scard" style="padding:16px;align-self:start;max-width:340px;width:100%">
                <div class="step-head"><span class="mdi mdi-calendar-search" style="color:var(--c-primary);font-size:22px"></span> {{ $t('privateBooking.viewByPeriod') }}</div>

                <!-- view-mode pills: รายวัน / รายเดือน -->
                <div class="pb-modes">
                    <button v-for="m in viewModes" :key="m.key" type="button"
                        class="pb-mode" :class="{ 'is-active': viewMode === m.key }" @click="setViewMode(m.key)">{{ m.label }}</button>
                </div>

                <!-- one calendar, always. The data fetch is the WHOLE displayed month, so dots (booking days)
                     always show for the whole month. รายวัน filters the grid to the tapped day (client-side,
                     instant); รายเดือน shows the whole month. Paging months (‹ ›) re-fetches via @month-change.
                     Mondays are pickable (this is a view filter, not a booking form). -->
                <id-calendar
                    :model-value="calendarModel"
                    :mark-keys="markKeys"
                    :disable-mondays="false"
                    :holidays="holidayDetails"
                    @update:model-value="onPickDay"
                    @month-change="onMonthChange">
                </id-calendar>
                <div class="pb-legend"><span class="pb-dot"></span> {{ $t('privateBooking.markLegend') }}</div>
            </div>
            <div>
                <id-data-grid
                    :columns="gridColumns"
                    :rows="displayRows"
                    :search-keys="['coachdisplay', 'studentsdisplay', 'classtime']"
                    :filters="gridFilters"
                    :search-placeholder="$t('btn.search')"
                    :loading="loadingBooking">
                    <template #actions>
                        <span v-if="viewers.length" class="pres-viewers"
                            v-hint="$t('presence.viewersHint', { names: viewers.join(', ') })"
                            :aria-label="$t('presence.viewersHint', { names: viewers.join(', ') })">
                            <span class="mdi mdi-account-eye"></span>{{ viewers.length }}
                        </span>
                        <button class="id-btn id-btn-ghost id-btn-sm" @click="getPrivateBookingList()">
                            <span class="mdi mdi-refresh"></span> {{ $t('btn.refresh') }}
                        </button>
                        <button class="id-btn id-btn-primary id-btn-sm" @click="showAddNewBooking">
                            <span class="mdi mdi-emoticon-plus-outline"></span> {{ $t('btn.newBooking') }}
                        </button>
                    </template>
                    <template #cell-privatetype="{ row }">
                        <span class="badge badge-info">{{ privateTypeLabel(row.privatetype) }}</span>
                    </template>
                    <!-- ราคาต้องมีทศนิยม 2 ตำแหน่งเหมือนทุกจุดในระบบ — ไม่มี slot นี้ IdDataGrid
                         จะพิมพ์ค่าดิบจาก DB ออกมาตรง ๆ -->
                    <template #cell-price="{ row }">
                        {{ $money(row.price) }}
                    </template>
                    <template #cell-paid="{ row }">
                        <span v-if="row.paid == 1" class="badge badge-success">{{ $t('customerCourse.paid') }}</span>
                        <span v-else class="badge badge-error">{{ $t('customerCourse.unpaid') }}</span>
                    </template>
                    <!-- เดิมเป็น mdi-eye-outline — เจ้าของสั่งเปลี่ยน 17 ส.ค.: dialog นี้กลายเป็น
                         "ข้อมูล + ประวัติการแก้ไข" แล้ว icon ประวัติ (นาฬิกาย้อน) สื่อความหมายตรงกว่าลูกตา -->
                    <template #cell-view="{ row }">
                        <span class="mdi mdi-history" style="color:var(--c-primary);cursor:pointer;font-size:20px" @click="showBookingInfo(row)"></span>
                    </template>
                    <template #cell-checkin="{ row }">
                        <span v-if="parseInt(row.checkedin) === 0" class="mdi mdi-check-bold" style="color:var(--c-success);cursor:pointer;font-size:20px" @click="clickCheckin(row)"></span>
                        <span v-else-if="parseInt(row.checkedin) === 1" class="mdi mdi-close-thick" style="color:var(--c-error);cursor:pointer;font-size:20px" @click="clickUndoCheckin(row)"></span>
                    </template>
                    <!-- ปุ่มดินสอเปิดได้เสมอแม้เช็คชื่อแล้ว — การบันทึกเงินหลังสอน (จ่ายทีหลัง) เป็นงานปกติ
                         ที่ทำผ่าน dialog นี้ทางเดียว · สิ่งที่ล็อกคือ "ฟิลด์กำหนดการ" ข้างใน dialog แทน
                         (ดู PrivateBookingEditDialog) + backend กันจริงที่ /updatePrivateBooking -->
                    <template #cell-edit="{ row }">
                        <span class="mdi mdi-pencil" style="color:var(--c-info);cursor:pointer;font-size:20px" @click="clickEditBooking(row)"></span>
                        <span v-if="lockedBy(row.privatebookingid)" class="pres-lock"
                            v-hint="$t('presence.editingHint', { name: lockedBy(row.privatebookingid) })"
                            :aria-label="$t('presence.editingHint', { name: lockedBy(row.privatebookingid) })"><span class="mdi mdi-pencil-circle"></span></span>
                    </template>
                    <template #cell-delete="{ row }">
                        <!-- กติกาเดียวกับคลาสกลุ่ม (audit B1 + เคาะ 2026-08-10) — backend กันซ้ำที่ /cancelPrivateBooking -->
                        <span v-if="parseInt(row.checkedin) === 1" class="mdi mdi-lock-outline"
                            style="color:var(--c-text-muted);font-size:20px" v-hint="$t('bookingMgmt.cancelNeedsUndo')"></span>
                        <span v-else-if="!canCancelRow(row)" class="mdi mdi-lock-outline"
                            style="color:var(--c-text-muted);font-size:20px" v-hint="$t('bookingMgmt.cancelAskManager')"></span>
                        <span v-else class="mdi mdi-delete-forever" style="color:var(--c-error);cursor:pointer;font-size:20px" @click="clickDeleteBooking(row)"></span>
                    </template>
                </id-data-grid>
            </div>
        </div>

        <!-- add/edit — shared with Dashboard.vue's quick "เพิ่มการจอง" flow -->
        <private-booking-edit-dialog :model-value="dialogPrivateBookingEdit" @update:model-value="onEditDialogToggle"
            :editing-booking="editingPrivateBooking"
            :locked-by="editingPrivateBooking ? lockedBy(editingPrivateBooking.privatebookingid) : null"
            @saved="getPrivateBookingList()" @onErrorHandler="onError($event)" @onInfoHandler="onShowInfoDialog($event)"
            @onLoading="onLoading($event)" @onClickChangeState="onClickChangeState($event)">
        </private-booking-edit-dialog>

        <id-modal v-model="dialogDelete" size="sm" icon="mdi-cancel" :title="$t('privateBooking.menuCancel')" persistent>
            <p style="margin:0">{{ $t('privateBooking.cancelConfirm', { name: selectedBooking ? coachDisplayName(selectedBooking) : '' }) }}</p>
            <template #footer>
                <button class="id-btn id-btn-ghost" @click="dialogDelete = false">{{ $t('btn.cancel') }}</button>
                <button class="id-btn id-btn-primary" style="background:var(--c-error)" @click="confirmDelete">
                    <span class="mdi mdi-delete"></span> {{ $t('btn.ok') }}</button>
            </template>
        </id-modal>

        <id-modal v-model="dialogCheckin" size="sm" icon="mdi-check-circle-outline" :title="$t('bookingList.menuCheckin')" persistent>
            <p style="margin:0">{{ $t('bookingMgmt.confirmCheckin', { name: selectedBooking ? studentNamesText(selectedBooking) : '' }) }}</p>
            <template #footer>
                <button class="id-btn id-btn-ghost" @click="dialogCheckin = false">{{ $t('btn.cancel') }}</button>
                <button class="id-btn id-btn-primary" @click="confirmCheckin"><span class="mdi mdi-check"></span> {{ $t('btn.ok') }}</button>
            </template>
        </id-modal>

        <id-modal v-model="dialogUndoCheckin" size="sm" icon="mdi-undo-variant" :title="$t('bookingList.menuUndoCheckin')" persistent>
            <p style="margin:0">{{ $t('bookingMgmt.confirmUndoCheckin', { name: selectedBooking ? studentNamesText(selectedBooking) : '' }) }}</p>
            <template #footer>
                <button class="id-btn id-btn-ghost" @click="dialogUndoCheckin = false">{{ $t('btn.cancel') }}</button>
                <button class="id-btn id-btn-primary" @click="confirmUndoCheckin"><span class="mdi mdi-check"></span> {{ $t('btn.ok') }}</button>
            </template>
        </id-modal>

        <!-- booking audit info — who booked/edited this Private session -->
        <id-modal v-model="dialogBookingInfo" size="sm" icon="mdi-information-outline"
            :title="$t('bookingMgmt.bookingInfoTitle')" :subtitle="bookingInfoItem ? studentNamesText(bookingInfoItem) : ''">
            <!-- เด็กที่เข้าเรียน — ชื่อเล่น + รูป (เจ้าของขอ 17 ส.ค.) · รูปโหลดจาก
                 /student/:id/profile-image รายคน (presigned) แบบเดียวกับปุ่มลูกตาหน้านักเรียน:
                 ไม่ await ตอนเปิด dialog, ล้มเงียบ → ไอคอนแทน · ใช้ <img> ธรรมดา ห้าม <v-img> -->
            <div class="pbv-students">
                <div v-for="s in (bookingInfoItem.students || [])" :key="s.studentid" class="pbv-student">
                    <div class="pbv-av">
                        <img v-if="viewStudentPhotos[s.studentid]" :src="viewStudentPhotos[s.studentid]" :alt="s.nickname || ''" />
                        <span v-else class="mdi mdi-account"></span>
                    </div>
                    <div class="pbv-name">{{ s.nickname || '-' }}</div>
                </div>
            </div>
            <div class="info-rows">
                <div class="info-row">
                    <span class="info-label"><span class="mdi mdi-whistle-outline"></span> {{ $t('privateBooking.coach') }}</span>
                    <span class="info-val">{{ coachDisplayName(bookingInfoItem) || '-' }}</span>
                </div>
                <div class="info-row">
                    <span class="info-label"><span class="mdi mdi-account-group-outline"></span> {{ $t('privateBooking.students') }}</span>
                    <span class="info-val">{{ studentNamesText(bookingInfoItem) }}</span>
                </div>
                <div class="info-row">
                    <span class="info-label"><span class="mdi mdi-account-plus-outline"></span> {{ $t('bookingMgmt.createBy') }}</span>
                    <span class="info-val">{{ bookingInfoItem.createby || '-' }}</span>
                </div>
                <div class="info-row">
                    <span class="info-label"><span class="mdi mdi-clock-plus-outline"></span> {{ $t('bookingMgmt.createDate') }}</span>
                    <span class="info-val">{{ format_datetime(bookingInfoItem.createdate) || '-' }}</span>
                </div>
                <template v-if="bookingInfoItem.updateby">
                    <div class="info-row">
                        <span class="info-label"><span class="mdi mdi-account-edit-outline"></span> {{ $t('bookingMgmt.updateBy') }}</span>
                        <span class="info-val">{{ bookingInfoItem.updateby }}</span>
                    </div>
                    <div class="info-row">
                        <span class="info-label"><span class="mdi mdi-clock-edit-outline"></span> {{ $t('bookingMgmt.editDate') }}</span>
                        <span class="info-val">{{ format_datetime(bookingInfoItem.editdate) || '-' }}</span>
                    </div>
                </template>
            </div>
            <edit-history-timeline v-if="dialogBookingInfo" kind="private" :refid="bookingInfoItem.privatebookingid"
                :preloaded="historyPreload"></edit-history-timeline>
            <template #footer>
                <button class="id-btn id-btn-primary" @click="dialogBookingInfo = false">{{ $t('btn.close') }}</button>
            </template>
        </id-modal>

        <id-modal v-model="errorDialog" size="sm" icon="mdi-alert-circle-outline" :title="$t('dialog.error')">
            <p style="margin:0">{{ errorMsg }}</p>
            <template #footer>
                <button class="id-btn id-btn-primary" @click="errorDialog = false">{{ $t('btn.close') }}</button>
            </template>
        </id-modal>

        <id-modal v-model="infoDialog" size="sm" icon="mdi-check-circle-outline" :title="$t('dialog.success')">
            <p style="margin:0">{{ infoMsg }}</p>
            <template #footer>
                <button class="id-btn id-btn-primary" @click="infoDialog = false">{{ $t('btn.ok') }}</button>
            </template>
        </id-modal>
    </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import PrivateBookingEditDialog from './PrivateBookingEditDialog.vue';
import presence from '@/utils/presence';
import { togglePrivateBookingCheckin } from '@/utils/privateBookingCheckin';
import { formatDateTimeBKK } from '@/utils/datetime';
import { formatMonthYear } from '@/utils/locale';
import { fetchEditHistory } from '@/utils/history';
import { preloadImage } from '@/utils/image';

export default {
    name: 'PrivateBookingManagement',
    components: { PrivateBookingEditDialog },
    props: {
        // 'YYYY-MM-DD' — เปิดหน้าที่วันนี้แทนวันนี้ ใช้ตอนกดการ์ดแจ้งเตือน (ต้องเห็นคาบที่ถูกแจ้ง
        // ทันที ไม่ใช่เปิดหน้าเปล่าของวันนี้) · null = ใช้วันนี้ตามเดิม
        initialDate: { type: String, default: null },
    },
    data() {
        return {
            errorDialog: false,
            errorMsg: '',
            infoDialog: false,
            infoMsg: '',
            interval: null,
            // the selected day AND the month anchor — the calendar always shows THIS month, and the
            // data fetch is always this whole month (so booking dots show for every day of the month)
            date: new Date(),
            // 'day' = grid shows the selected day's bookings | 'month' = grid shows the whole month.
            // Both read from the same month fetch — switching is an instant client-side filter, no refetch.
            viewMode: 'day',
            privateBookingList: [],
            loadingBooking: false,
            dialogPrivateBookingEdit: false,
            editingPrivateBooking: null, // null = add new; a row object = edit that booking
            viewers: [],       // สตาฟคนอื่นที่เปิดจอนี้อยู่
            editors: {},       // privatebookingid -> username ที่เปิด dialog แก้ใบนั้นอยู่
            offPresence: null,
            presenceToken: 0,
            dialogCheckin: false,
            dialogUndoCheckin: false,
            dialogDelete: false,
            selectedBooking: null,
            dialogBookingInfo: false,
            bookingInfoItem: {},
            historyPreload: null,   // ไทม์ไลน์การแก้ไขที่โหลดไว้ก่อนเปิด dialog ข้อมูลใบจอง
            // studentid → presigned URL ของรูป สำหรับ dialog ปุ่มลูกตา (ล้างทุกครั้งที่เปิดใหม่)
            viewStudentPhotos: {},
            // [{ holidaydate, description }] from /holidaysList — marks holiday days on the calendar
            holidayDetails: [],
        };
    },
    async created() {
        // ตั้งวันก่อนโหลด ไม่งั้นจะยิง fetch ของเดือนปัจจุบันทิ้งไปหนึ่งรอบก่อนแล้วค่อยยิงเดือนที่ถูก
        // สร้างเป็น local date จาก 3 ส่วน ห้าม new Date('YYYY-MM-DD') — อันนั้นถูกอ่านเป็น UTC
        // แล้วเลื่อนเป็นวันก่อนหน้าในโซนเวลาไทย (กับดักเดิมของระบบ)
        if (this.initialDate && /^\d{4}-\d{2}-\d{2}$/.test(this.initialDate)) {
            const [y, m, d] = this.initialDate.split('-').map(Number);
            this.date = new Date(y, m - 1, d);
        }
        this.$emit('onLoading', true);
        await this.getPrivateBookingList();
        this.$emit('onLoading', false);
        this.fetchHolidays();
    },
    mounted() {
        this.interval = setInterval(() => {
            this.getPrivateBookingList(true); // background poll — never wipe the grid or pop an error dialog
        }, 60000);
        // ป้าย "ใครเปิดอยู่/ใบไหนมีคนแก้" — เตือนอย่างเดียว ไม่บล็อกปุ่มบันทึก
        this.presenceToken = presence.enter('privatebookingmanager');
        this.offPresence = presence.subscribe((p) => { this.viewers = p.viewers; this.editors = p.editors; });
    },
    beforeUnmount() {
        clearInterval(this.interval);
        presence.leave(this.presenceToken);
        if (this.offPresence) this.offPresence();
    },
    computed: {
        myUsername() {
            const u = this.$store.getters.getUserdata;
            return (u && u.username) || '';
        },
        viewModes() {
            return [
                { key: 'day', label: this.$t('privateBooking.periodDay') },
                { key: 'month', label: this.$t('privateBooking.periodMonth') },
            ];
        },
        // the fetched span = the whole month containing `date`
        monthStart() { return moment(this.date).startOf('month').toDate(); },
        monthEnd() { return moment(this.date).endOf('month').toDate(); },
        // what IdCalendar shows selected/anchored (non-padded 'YYYY-M-D' like its keyOf)
        calendarModel() {
            return this.date ? (this.date.getFullYear() + '-' + (this.date.getMonth() + 1) + '-' + this.date.getDate()) : '';
        },
        // the selected day as an SQL key, for the day-mode client-side filter
        selectedKey() { return this.SQLDate(this.date); },
        // กติกายกเลิก (เจ้าของเคาะ 2026-08-10): ผู้จัดการยกเลิกได้ทุกใบ — บทบาทอื่นติดกรอบ 3 วัน
        isHead() {
            const u = this.$store.getters.getUserdata;
            return String(u && u.usertype) === '0';
        },
        // booking days in the fetched month, keyed 'YYYY-M-D' to match IdCalendar's dot keys — ALWAYS
        // shown (the fetch is the whole month), regardless of day/month view mode
        markKeys() {
            return [...new Set(this.privateBookingList.map((b) => moment(b.bookingdate).format('YYYY-M-D')))];
        },
        // quick totals for the month overview chip (month mode)
        summary() {
            const rows = this.displayRows;
            const coaches = new Set(rows.map((r) => r.coachdisplay).filter(Boolean));
            const days = new Set(rows.map((r) => moment(r.bookingdate).format('YYYY-MM-DD')));
            return { count: rows.length, coaches: coaches.size, days: days.size };
        },
        // datebar label — day: the selected date (DD/MM/YYYY, matches the grid); month: Thai Buddhist month name
        rangeLabel() {
            if (this.viewMode === 'month') return formatMonthYear(this.date);
            return this.format_date(this.date);
        },
        gridColumns() {
            return [
                { title: this.$t('privateBooking.coach'), key: 'coachdisplay' },
                { title: this.$t('privateBooking.type'), key: 'privatetype' },
                // bookingdatedisplay is 'DD/MM/YYYY' — IdDataGrid's sort is now date-aware (it parses
                // DD/MM/YYYY chronologically), so sorting by date is safe again.
                { title: this.$t('table.date'), key: 'bookingdatedisplay' },
                { title: this.$t('table.classTime'), key: 'classtime' },
                { title: this.$t('privateBooking.students'), key: 'studentsdisplay', sortable: false },
                { title: this.$t('privateBooking.hours'), key: 'hours' },
                { title: this.$t('privateBooking.price'), key: 'price' },
                { title: this.$t('table.payment'), key: 'paid', align: 'center', sortable: false },
                { title: this.$t('table.view'), key: 'view', align: 'center', sortable: false },
                { title: this.$t('table.checkin'), key: 'checkin', align: 'center', sortable: false },
                { title: this.$t('table.edit'), key: 'edit', align: 'center', sortable: false },
                { title: this.$t('table.delete'), key: 'delete', align: 'center', sortable: false },
            ];
        },
        // flatten fields the grid searches/filters on, keep rows chronological (date then classtime),
        // and in day mode narrow the fetched month down to just the selected day (client-side, instant)
        displayRows() {
            const rows = this.privateBookingList
                .filter((b) => this.viewMode === 'month' || this.SQLDate(b.bookingdate) === this.selectedKey)
                .map((b) => ({
                    ...b,
                    coachdisplay: this.coachDisplayName(b),
                    bookingdatedisplay: this.format_date(b.bookingdate),
                    studentsdisplay: this.studentNamesText(b),
                }))
                .sort((a, b) => {
                    const t = moment(a.bookingdate).valueOf() - moment(b.bookingdate).valueOf();
                    return t !== 0 ? t : String(a.classtime || '').localeCompare(String(b.classtime || ''));
                });
            return rows;
        },
        gridFilters() {
            const coaches = [...new Set(this.displayRows.map((r) => r.coachdisplay).filter(Boolean))].map((c) => ({ value: c, label: c }));
            return [
                { key: 'coachdisplay', label: this.$t('privateBooking.coach'), options: coaches },
                { key: 'privatetype', label: this.$t('privateBooking.type'), options: [
                    { value: '1on1', label: this.$t('customerCourse.private1on1') },
                    { value: '2on1', label: this.$t('customerCourse.private2on1') },
                    { value: '3on1', label: this.$t('customerCourse.private3on1') },
                ] },
                { key: 'paid', label: this.$t('table.payment'), options: [
                    { value: 1, label: this.$t('customerCourse.paid') },
                    { value: 0, label: this.$t('customerCourse.unpaid') },
                ] },
                { key: 'checkedin', label: this.$t('table.checkin'), options: [
                    { value: 1, label: this.$t('bookingList.menuCheckedInBadge') },
                    { value: 0, label: this.$t('bookingList.notCheckedIn') },
                ] },
            ];
        },
    },
    methods: {
        async fetchHolidays() {
            try {
                const token = this.$store.getters.getToken;
                const res = await axios.get(this.baseURL + '/holidaysList', { headers: { Authorization: `Bearer ${token}` } });
                if (res.data.success) this.holidayDetails = res.data.details || [];
            } catch (e) { /* holidays are non-critical for the booking calendar */ }
        },
        SQLDate(date) {
            return moment(date).format('YYYY-MM-DD');
        },
        format_date(value) {
            return value ? moment(value).format('DD/MM/YYYY') : '';
        },
        // แสดง createdate/editdate (TIMESTAMP จาก backend) เป็นเวลาไทยคงที่ ไม่ขึ้นกับ timezone ของเครื่องผู้ใช้
        format_datetime(value) {
            return formatDateTimeBKK(value, 'DD/MM/YYYY HH:mm');
        },
        // เปิด dialog แสดงว่าใครจอง/แก้ไขรอบ Private นี้ (mirrors BookingManagement.vue's showBookingInfo)
        // เตรียมทุกอย่างที่อยู่ใน dialog (ไทม์ไลน์ + รูปเด็ก) ให้เสร็จก่อนเปิด — เดิมเปิดก่อนแล้วค่อย
        // เติมทีละชิ้น ผู้ใช้เห็นเป็นหน้าจอกระพริบ
        async showBookingInfo(row) {
            this.bookingInfoItem = Object.assign({}, row);
            this.viewStudentPhotos = {};
            this.$emit('onLoading', true);
            try {
                const [history] = await Promise.all([
                    fetchEditHistory(this.baseURL, this.$store.getters.getToken, 'private', row.privatebookingid),
                    // ไม่มีรูป/โหลดพลาดเป็นเรื่องปกติ (loadStudentPhoto ล้มเงียบ) โชว์ไอคอนแทน
                    ...(row.students || []).map((s) => this.loadStudentPhoto(row.privatebookingid, s.studentid)),
                ]);
                this.historyPreload = history;
            } finally {
                this.$emit('onLoading', false);
            }
            this.dialogBookingInfo = true;
        },
        async loadStudentPhoto(bookingId, studentid) {
            if (!studentid) return;
            try {
                const response = await axios.get(
                    this.baseURL + `/student/${studentid}/profile-image`,
                    { headers: { Authorization: `Bearer ${this.$store.getters.getToken}` } }
                );
                // ทิ้งผลที่มาช้าถ้าผู้ใช้เปิดใบจองอื่นไปแล้ว — กันรูปไปโผล่ผิดใบ
                if (this.bookingInfoItem.privatebookingid !== bookingId) return;
                if (response.data.success && response.data.imageUrl) {
                    // โหลดรูปเข้าแคชก่อนค่อยผูก src — ไม่งั้นเห็นไอคอนคนแว๊บก่อนรูปจริงมา
                    await preloadImage(response.data.imageUrl);
                    if (this.bookingInfoItem.privatebookingid !== bookingId) return;
                    // สร้าง object ใหม่แทน mutate เพื่อให้ Vue เห็นการเปลี่ยนแปลงแน่ ๆ
                    this.viewStudentPhotos = { ...this.viewStudentPhotos, [studentid]: response.data.imageUrl };
                }
            } catch (e) { /* ล้มเงียบ — dialog ต้องเปิดได้เสมอ */ }
        },
        coachDisplayName(booking) {
            return (booking.coachname && booking.coachname.trim()) || booking.coachusername || '';
        },
        privateTypeLabel(privatetype) {
            const map = {
                '1on1': 'customerCourse.private1on1',
                '2on1': 'customerCourse.private2on1',
                '3on1': 'customerCourse.private3on1',
            };
            return map[privatetype] ? this.$t(map[privatetype]) : privatetype;
        },
        studentNamesText(booking) {
            const names = (booking.students || []).map((s) => s.nickname || s.fullname || s.name).filter(Boolean);
            let text = names.join(', ');
            if (booking.othersCount) {
                const othersText = this.$t('privateBooking.othersCount', { n: booking.othersCount });
                text = text ? `${text} + ${othersText}` : othersText;
            }
            return text || '-';
        },
        // switch day/month — no refetch: both views read from the same month already in memory
        setViewMode(mode) {
            this.viewMode = mode;
        },
        // tapped a day on the calendar — select it (day mode narrows the grid to it). The calendar only
        // renders the current month's days, so this never crosses months; refetch only defensively.
        onPickDay(key) {
            const p = key.split('-');
            const d = new Date(+p[0], +p[1] - 1, +p[2]);
            const monthChanged = d.getFullYear() !== this.date.getFullYear() || d.getMonth() !== this.date.getMonth();
            this.date = d;
            if (monthChanged) this.getPrivateBookingList();
        },
        // paged the calendar to another month (‹ › / month picker) — move the anchor there (clamping the
        // day so e.g. Jan 31 → Feb 28) and refetch that month's data + dots
        onMonthChange({ year, month }) {
            const dim = moment([year, month]).daysInMonth();
            this.date = new Date(year, month, Math.min(this.date.getDate(), dim));
            this.getPrivateBookingList();
        },
        goToday() {
            this.date = new Date();
            this.getPrivateBookingList();
        },
        // silent=true for the 60s background poll — a transient/stale failure must not wipe the grid
        // or pop the App-level error dialog (only 401 still forces re-login). Explicit user actions
        // (created, month change, today, Refresh) pass silent=false and keep the original error UX.
        async getPrivateBookingList(silent = false) {
            // always fetch the WHOLE displayed month, so booking dots show for every day of the month;
            // day/month view mode only changes which rows the grid shows (client-side filter)
            const startdate = this.SQLDate(this.monthStart);
            const enddate = this.SQLDate(this.monthEnd);
            // bookingdate (= month start) is sent too so an older backend without range support still answers
            const payload = { bookingdate: startdate, startdate, enddate };
            const sig = startdate + '|' + enddate;
            // silent poll updates the grid in place — no skeleton swap (which would flicker the table
            // every 60s and hide the pager/scroll position). Only user-initiated fetches show the skeleton.
            if (!silent) this.loadingBooking = true;
            const t0 = Date.now();
            try {
                const token = this.$store.getters.getToken;
                const response = await axios.post(this.baseURL + '/getPrivateBookingList', payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                // month changed again mid-flight — drop the now-stale response
                if (sig !== this.SQLDate(this.monthStart) + '|' + this.SQLDate(this.monthEnd)) return;
                if (response.data.success) {
                    this.privateBookingList = response.data.bookinglist || [];
                } else if (!silent) {
                    this.privateBookingList = [];
                    this.$emit('onErrorHandler', response.data.message || this.$t('msg.loadFail'));
                }
            } catch (error) {
                // stale rejection after a month switch — ignore (a newer request is in flight)
                if (sig !== this.SQLDate(this.monthStart) + '|' + this.SQLDate(this.monthEnd)) return;
                if (error.response && error.response.status === 401) {
                    this.privateBookingList = [];
                    this.$emit('onErrorHandler', error.response.data.message);
                    this.$emit('onClickChangeState', 'login');
                } else if (!silent) {
                    this.privateBookingList = [];
                    this.$emit('onErrorHandler', error.message);
                }
            } finally {
                if (!silent) {
                    await this.$minLoad(t0);
                    this.loadingBooking = false;
                }
            }
        },
        showAddNewBooking() {
            this.editingPrivateBooking = null;
            this.dialogPrivateBookingEdit = true;
        },
        // ใบใหม่ไม่มีเลขที่ให้ล็อก — ปล่อยล็อกเสมอตอนปิด dialog ไม่ว่าจะมาจากทางไหน
        onEditDialogToggle(open) {
            this.dialogPrivateBookingEdit = open;
            if (!open) presence.edit(null);
        },
        // ใครแก้ใบนี้อยู่ (ไม่นับตัวเอง — ด่านที่สอง ฝั่งเซิร์ฟเวอร์กรองให้แล้วแต่อย่าพึ่งสัญญาฝั่งเดียว)
        lockedBy(id) {
            const who = this.editors[id];
            return who && who !== this.myUsername ? who : null;
        },
        clickEditBooking(row) {
            this.editingPrivateBooking = row;
            this.dialogPrivateBookingEdit = true;
            presence.edit(row.privatebookingid);   // สายขาดเมื่อไหร่ล็อกหายเอง
        },
        clickCheckin(row) {
            this.selectedBooking = row;
            this.dialogCheckin = true;
        },
        clickUndoCheckin(row) {
            this.selectedBooking = row;
            this.dialogUndoCheckin = true;
        },
        async confirmCheckin() {
            this.dialogCheckin = false;
            await this.doCheckinToggle(false);
        },
        async confirmUndoCheckin() {
            this.dialogUndoCheckin = false;
            await this.doCheckinToggle(true);
        },
        async doCheckinToggle(wasCheckedIn) {
            if (!this.selectedBooking) return;
            this.$emit('onLoading', true);
            try {
                const token = this.$store.getters.getToken;
                const data = await togglePrivateBookingCheckin(this.baseURL, token, this.selectedBooking.privatebookingid, wasCheckedIn);
                if (data.success) {
                    await this.getPrivateBookingList();
                } else {
                    this.$emit('onErrorHandler',
                        (data && data.message) ||
                        (wasCheckedIn ? this.$t('bookingList.undoCheckinFail') : this.$t('bookingList.checkinFail')));
                }
            } catch (error) {
                if (error.response && error.response.status === 401) {
                    this.$emit('onErrorHandler', error.response.data.message);
                    this.$emit('onClickChangeState', 'login');
                } else {
                    this.$emit('onErrorHandler', error.message);
                }
            } finally {
                this.$emit('onLoading', false);
            }
        },
        clickDeleteBooking(row) {
            this.selectedBooking = row;
            this.dialogDelete = true;
        },
        // เกิน 3 วันหลังวันเรียน = บทบาทอื่นนอกจากผู้จัดการยกเลิกไม่ได้ (เจ้าของเคาะ 2026-08-10)
        // backend บังคับกติกาเดียวกันซ้ำที่ /cancelPrivateBooking เสมอ
        canCancelRow(row) {
            if (this.isHead) return true;
            if (!row.bookingdate) return true;
            return moment(row.bookingdate).isSameOrAfter(moment().subtract(3, 'days'), 'day');
        },
        async confirmDelete() {
            this.dialogDelete = false;
            if (!this.selectedBooking) return;
            this.$emit('onLoading', true);
            try {
                const token = this.$store.getters.getToken;
                const response = await axios.post(this.baseURL + '/cancelPrivateBooking', {
                    privatebookingid: this.selectedBooking.privatebookingid,
                }, { headers: { Authorization: `Bearer ${token}` } });
                if (response.data.success) {
                    this.$emit('onInfoHandler', response.data.message || this.$t('privateBooking.cancelSuccess'));
                    await this.getPrivateBookingList();
                } else {
                    this.$emit('onErrorHandler', response.data.message || this.$t('privateBooking.cancelFail'));
                }
            } catch (error) {
                if (error.response && error.response.status === 401) {
                    this.$emit('onErrorHandler', error.response.data.message);
                    this.$emit('onClickChangeState', 'login');
                } else {
                    this.$emit('onErrorHandler', error.message);
                }
            } finally {
                this.$emit('onLoading', false);
            }
        },
        onClickChangeState(state) {
            this.$emit('onClickChangeState', state);
        },
        onError(msg) {
            this.$emit('onErrorHandler', msg);
        },
        onLoading(val) {
            this.$emit('onLoading', val);
        },
        onShowInfoDialog(msg) {
            this.infoMsg = msg;
            this.infoDialog = true;
        },
    },
};
</script>

<style scoped>

/* เด็กที่เข้าเรียนใน dialog ปุ่มลูกตา — วงกลมรูป + ชื่อเล่นใต้รูป (ย่อส่วนจาก .dlg-avatar/.av-ph
   ซึ่งเป็น single-avatar 96px ของฟอร์มแก้ไข — ที่นี่มีได้ถึง 3 คนเรียงแถวจึงใช้ 64px) */
.pbv-students { display: flex; justify-content: center; gap: 18px; margin-bottom: 16px; flex-wrap: wrap; }
.pbv-student { display: flex; flex-direction: column; align-items: center; gap: 6px; }
.pbv-av {
  width: 64px; height: 64px; border-radius: 50%; overflow: hidden;
  background: var(--c-surface-3); border: 3px solid var(--c-border-strong);
  display: grid; place-items: center; color: var(--c-primary-light);
}
.pbv-av .mdi { font-size: 32px; }
.pbv-av img { width: 100%; height: 100%; object-fit: cover; }
.pbv-name { font-size: 13px; font-weight: 600; color: var(--c-text-body); max-width: 84px; text-align: center; }

/* view-mode pills (รายวัน / รายเดือน) — mirror IdCalendar's .cal-mon look so they feel like the same
   system; active pill filled with the brand pink like the footer's active pager */
.pb-modes { display: flex; gap: 6px; margin-bottom: 12px; }
.pb-mode {
  flex: 1; height: 32px; padding: 0 6px;
  border: 1px solid var(--c-border); border-radius: var(--radius-sm);
  background: var(--c-surface); color: var(--c-text-body);
  font-family: var(--font-body); font-size: 13px; cursor: pointer;
  transition: background var(--dur-fast) var(--ease), color var(--dur-fast) var(--ease), border-color var(--dur-fast) var(--ease);
}
.pb-mode:hover:not(.is-active) { border-color: var(--c-primary); color: var(--c-primary); }
.pb-mode.is-active { background: var(--c-primary); border-color: var(--c-primary); color: #fff; font-weight: 700; }

/* booking-day dot legend under the calendar */
.pb-legend { display: flex; align-items: center; gap: 6px; margin-top: 10px; font-size: 11.5px; color: var(--c-text-muted); }
.pb-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--c-primary); display: inline-block; }

/* "วันนี้" quick-jump button in the reserve-datebar */
.pb-today {
  height: 26px; padding: 0 10px; border: 1px solid var(--c-border); background: var(--c-surface);
  color: var(--c-text-body); border-radius: var(--radius-sm); font-family: var(--font-body); font-size: 12px; cursor: pointer;
}
.pb-today:hover { border-color: var(--c-primary); color: var(--c-primary); }
</style>
